import { useState } from 'react'
import {
    Box, Button, Typography, Modal, TextField, useMediaQuery,
    FormControl, InputLabel, Select, MenuItem, FormHelperText,
} from '@mui/material'

import Swal from 'sweetalert2'

import { isNsfwUrl, NSFW_WARNING } from '../../../../../../utils/nsfwGuard'
import { isValidLinkUrl, INVALID_URL_WARNING } from '../../../../../../utils/urlValidation'
import { startCheckout } from '../../../../../../services/api/StripeDataService'

const MONEY_TYPES = ['SPONSOR', 'SHOP', 'AFFILIATE', 'OTHER']

export function AddLinkButonModal({ open, onClose, onAddLink, isPro, onSetMoneyType }) {
    const [title, setTitle] = useState('')
    const [url, setUrl] = useState('')
    const [moneyType, setMoneyType] = useState('')
    const [urlError, setUrlError] = useState('')
    const isMobile = useMediaQuery('(max-width:1050px)')

    const handleUpgrade = async () => {
        try {
            await startCheckout('pro', true) // redirects to the Stripe Pro checkout
        } catch (error) {
            Swal.fire({
                title: "Can't Proceed to Checkout",
                text: 'Something went wrong. Please try again later.',
                icon: 'error',
            })
        }
    }

    const handleAddLink = async () => {
        if (title && url) {
            // Only allow real links — reject code snippets / non-URL input.
            if (!isValidLinkUrl(url)) {
                setUrlError(INVALID_URL_WARNING)
                return
            }
            // Block links to NSFW / 18+ sites before anything is saved.
            if (isNsfwUrl(url)) {
                setUrlError(NSFW_WARNING)
                return
            }
            const created = await onAddLink({ title, url })
            // The link's uuid only exists after creation, so tag it afterwards.
            if (isPro && moneyType && created?.uuid) {
                await onSetMoneyType?.(created.uuid, moneyType)
            }
            setTitle('')
            setUrl('')
            setMoneyType('')
            setUrlError('')
            onClose()
        }
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: isMobile ? 300 : 400,
                bgcolor: 'background.paper',
                borderRadius: '30px',
                boxShadow: 24,
                p: 4,
            }}>
                <Typography id="modal-modal-title" variant="h6" component="h2">
                    Add new link
                </Typography>
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="title"
                    label="Title"
                    name="title"
                    autoComplete="title"
                    autoFocus
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                />
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="url"
                    label="URL"
                    name="url"
                    autoComplete="url"
                    value={url}
                    error={!!urlError}
                    helperText={urlError}
                    onChange={(e) => { setUrl(e.target.value); setUrlError('') }}
                />

                <FormControl fullWidth margin="normal" disabled={!isPro}>
                    <InputLabel id="money-type-label">Revenue Link</InputLabel>
                    <Select
                        labelId="money-type-label"
                        label="Revenue Link"
                        value={moneyType}
                        onChange={(e) => setMoneyType(e.target.value)}
                        displayEmpty
                    >
                        {MONEY_TYPES.map((t) => (
                            <MenuItem key={t} value={t}>{t.charAt(0) + t.slice(1).toLowerCase()}</MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                        {isPro
                            ? "Revenue Links help you track which earning links get the most clicks."
                            : (
                                <>
                                    Revenue Links help you track which earning links get the most clicks.{' '}
                                    <Box
                                        component="span"
                                        onClick={handleUpgrade}
                                        sx={{
                                            color: 'primary.main',
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                            pointerEvents: 'auto',
                                            '&:hover': { color: 'primary.dark' },
                                        }}
                                    >
                                        Upgrade to Pro
                                    </Box>{' '}
                                    to start tracking.
                                </>
                            )
                        }
                    </FormHelperText>
                </FormControl>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddLink}
                    fullWidth
                    sx={{
                        display: 'block',
                        mx: 'auto',
                        mt: 2,
                        padding: '.6rem 1rem',
                        borderRadius: '40px',
                        boxShadow: "2px 3px 0px #000",
                        textTransform: 'none'
                    }}
                >
                    Add
                </Button>
            </Box>
        </Modal>
    )
}
